import {
  BENCHMARK_BAND_KEYS,
  BENCHMARK_BAND_LABELS,
  MetricRange,
  MetricRangeMap,
  cleanBenchmarkBandRangeMap,
} from '../../helpers/metricRules';

export type BenchmarkField =
  | 'average'
  | 'min'
  | 'max'
  | 'count'
  | 'sum'
  | 'median'
  | 'variance'
  | 'stddev';

export type BenchmarkBandKey = (typeof BENCHMARK_BAND_KEYS)[number];

export type BenchmarkBandMatch = {
  band: BenchmarkBandKey;
  label: string;
  score: number;
  inRange: boolean;
  range?: MetricRange;
};

type MetricCandidateIndex = Map<string, any[]>;

export const BENCHMARK_FIELD_LABELS: Record<BenchmarkField, string> = {
  average: 'Avg',
  min: 'Min',
  max: 'Max',
  count: 'Count',
  sum: 'Sum',
  median: 'Median',
  variance: 'Variance',
  stddev: 'Std Dev',
};

export const DEFAULT_BENCHMARK_FIELDS: BenchmarkField[] = ['average'];

export const normalizeBenchmarkField = (field: string): BenchmarkField | null => {
  if (field === 'avg') return 'average';
  if (Object.prototype.hasOwnProperty.call(BENCHMARK_FIELD_LABELS, field)) {
    return field as BenchmarkField;
  }
  return null;
};

export const getBenchmarkFieldValue = (row: any, field: BenchmarkField): number | null => {
  const value = field === 'average' ? row?.average ?? row?.avg : row?.[field];
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return value;
};

export const formatBenchmarkValue = (value?: number | null, unit?: string | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  const formatted = Number(value).toFixed(2);
  return unit ? `${formatted} ${unit}` : formatted;
};

const normalizeLookupKey = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

export const extractBaseMetricKey = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const key = value.trim();
  if (!key) return null;

  const match = key.match(/-(avg|average|min|max|count|sum|median|variance|stddev)(-|$)/i);
  if (!match || typeof match.index !== 'number' || match.index <= 0) {
    return key;
  }

  const base = key.slice(0, match.index).trim();
  return base || key;
};

export const buildBenchmarkMetricCandidateIndex = (metrics: any[]): MetricCandidateIndex => {
  const index: MetricCandidateIndex = new Map();
  const add = (rawKey: unknown, metric: any) => {
    const key = normalizeLookupKey(rawKey);
    if (!key) return;
    const list = index.get(key) || [];
    if (!list.includes(metric)) {
      list.push(metric);
      index.set(key, list);
    }
  };

  (Array.isArray(metrics) ? metrics : []).forEach((metric: any) => {
    add(metric?.key, metric);
    add(metric?.sourceKey, metric);
    add(metric?.name, metric);
    add(extractBaseMetricKey(metric?.key), metric);
  });

  return index;
};

export const findBenchmarkMetricCandidates = (
  index: MetricCandidateIndex,
  rawKeys: Array<unknown>
): any[] => {
  const uniqueCandidates: any[] = [];
  const seen = new Set<any>();

  rawKeys.forEach((rawKey) => {
    const normalized = normalizeLookupKey(rawKey);
    if (!normalized) return;
    const candidates = index.get(normalized) || [];
    candidates.forEach((candidate) => {
      if (!seen.has(candidate)) {
        seen.add(candidate);
        uniqueCandidates.push(candidate);
      }
    });
  });

  return uniqueCandidates;
};

const isValueInsideRange = (value: number, range: MetricRange): boolean => {
  const min = typeof range.min === 'number' ? range.min : null;
  const max = typeof range.max === 'number' ? range.max : null;
  if (min !== null && value < min) return false;
  if (max !== null && value > max) return false;
  return true;
};

const distanceToRange = (value: number, range: MetricRange): number => {
  const min = typeof range.min === 'number' ? range.min : null;
  const max = typeof range.max === 'number' ? range.max : null;
  if (min === null && max === null) return Number.POSITIVE_INFINITY;
  if (min !== null && value < min) return min - value;
  if (max !== null && value > max) return value - max;
  return 0;
};

export const resolveBenchmarkBand = (
  value: number | null | undefined,
  benchmarkRanges?: MetricRangeMap
): BenchmarkBandMatch | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const cleaned = cleanBenchmarkBandRangeMap(benchmarkRanges);
  if (!cleaned) return null;

  for (const bandKey of BENCHMARK_BAND_KEYS) {
    const range = cleaned[bandKey];
    if (!range) continue;
    if (isValueInsideRange(value, range)) {
      return {
        band: bandKey,
        label: BENCHMARK_BAND_LABELS[bandKey],
        score: Number(bandKey),
        inRange: true,
        range,
      };
    }
  }

  let nearestBand: BenchmarkBandKey | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  BENCHMARK_BAND_KEYS.forEach((bandKey) => {
    const range = cleaned[bandKey];
    if (!range) return;
    const distance = distanceToRange(value, range);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestBand = bandKey;
    }
  });

  if (!nearestBand) return null;
  return {
    band: nearestBand,
    label: BENCHMARK_BAND_LABELS[nearestBand],
    score: Number(nearestBand),
    inRange: false,
    range: cleaned[nearestBand],
  };
};
